import { AnyAction } from 'redux';
import { STATE } from 'app/constants';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import * as navigate from 'app/area/shared/navigation';
import { isForbiddenStatus, blockAccess, isNotFoundStatus } from 'app/area/shared/actions-utils';
import { mapFavouriteFromWire, mapFavouriteToWire } from './utils/map-favourite';
import { favouriteAdminTranslation } from '../constants';
import {
  RequestLanguageOptions, ReceiveLanguageOptions, FailLanguageOptions,
  RequestFavourite, ReceiveFavourite, FailFavourite,
  RequestDeleteFavourite, ReceiveDeleteFavourite, FailDeleteFavourite,
  UpdateItem, ChangeLanguage, CopyLanguageToAll, ClearForm, UpdateValidation,
  ResetValidation, ReceiveDeleteSchedule, RequestDeleteSchedule, FailDeleteSchedule,
  RequestSaveFavourite, ReceiveSaveFavourite, FailSaveFavourite, ChangeLongDescription, CopyLongDescriptionToAll
} from './action-types';
import { api } from './api';
import { getBackRoute } from './route';
import { FavouriteItem, FavouriteItemValidation } from './types-store';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { complexValidationFromServer, ServerValidationErrors } from 'app/infrastructure/data/validation';

const serverValidationMessages = (validationErrors: ServerValidationErrors): string[] =>
  Object.values(validationErrors ?? {})
    .flatMap(messages => messages.map(message => typeof message === 'string' ? message : message.message));

export const actionCreators = {

  loadLanguageOptions: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestLanguageOptions());

      try {

        const languageOptions = await api.loadLanguageOptions();

        dispatch(new ReceiveLanguageOptions(
          languageOptions.map(option => ({
            value: option.v,
            description: option.d
          }))
        ));
      } catch (err) {

        if (err && (err.userMessages || err.message)) {
          dispatch(new ErrorNotification(err.userMessages || err.message));
        }
        dispatch(new FailLanguageOptions());
      }
    },

  loadFavourite: (id: number): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestFavourite(id));

      try {

        const favourite = await api.loadFavourite(id);

        const favouriteItem = mapFavouriteFromWire(favourite);

        dispatch(new ReceiveFavourite(id, favouriteItem));

      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.favouriteAdministration,
            translationId: favouriteAdminTranslation.no_access_to_favourite_admin
          });
        } else {

          const errorMessage = err && (err.userMessages || err.message);
          !!errorMessage && dispatch(new ErrorNotification(errorMessage));

          navigate.to({ pathname: getBackRoute() });
        }

        dispatch(new FailFavourite(id));
      }
    },

  deleteFavourite: (id: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestDeleteFavourite(id));

      try {
        await api.deleteFavourite(id);

        navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });

        dispatch(new ReceiveDeleteFavourite(id));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailDeleteFavourite(id));

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        }
      }
    },

  saveFavourite: (
    favourite: FavouriteItem,
    successMessage: string,
    validationErrorMessage: string,
    addOwnersToDescendants?: boolean
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const id = favourite?.id;

      dispatch(new RequestSaveFavourite(id));

      try {

        const favouriteWire = mapFavouriteToWire(favourite);

        if (isDefinedIncludingZero(id)) {

          await api.saveFavourite(id, favouriteWire, addOwnersToDescendants);

          dispatch(new ReceiveSaveFavourite(id, undefined));
        }

        navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        dispatch(new SuccessNotification(successMessage));

      } catch (err) {

        if (err?.userMessages?.length ?? 0 > 0) {
          dispatch(new ErrorNotification(err.userMessages));
        }

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          const messages = serverValidationMessages(err.validationErrors);
          dispatch(new ErrorNotification(messages.length > 0 ? messages.join('\n') : validationErrorMessage));
          dispatch(new UpdateValidation(complexValidationFromServer(err.validationErrors)));
        }

        dispatch(new FailSaveFavourite(id));

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        }
      }
    },

  deleteSchedule: (id: number, successMessage: string,
    hideRemoveButton: (value: boolean) => void): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestDeleteSchedule(id));

      try {
        await api.deleteSchedule(id);
        hideRemoveButton(true);

        dispatch(new ReceiveDeleteSchedule(id));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailDeleteSchedule(id));
        hideRemoveButton(false);
      }
    },

  clearForm: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearForm());
    },

  updateItem: (item: Partial<FavouriteItem>): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateItem(item));
    },

  changeLanguage: (languageId: number, text: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ChangeLanguage(languageId, text));
    },

  copyLanguageToAll: (languageId: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new CopyLanguageToAll(languageId));
      dispatch(new SuccessNotification(successMessage));
    },

  changeLongDescription: (languageId: number, text: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ChangeLongDescription(languageId, text));
    },

  copyLongDescriptionToAll: (languageId: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new CopyLongDescriptionToAll(languageId));
      dispatch(new SuccessNotification(successMessage));
    },

  updateValidation: (validation: FavouriteItemValidation): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateValidation(validation));
    },

  resetValidation: (key: string): ActionDispatching<AnyAction, ApplicationState> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(key));
  },

  displayError: (errorMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(errorMessage));
    }
};
